import { useEffect } from 'react';

/**
 * Mark this page as dark-themed for as long as it is mounted.
 *
 * tailwind.config.js sets `darkMode: ['class']`, so Tailwind's dark: variants
 * only apply while a `dark` class sits on an ancestor. Five pages set that
 * class on document.documentElement with the same copied block:
 *
 *     useEffect(() => {
 *       document.documentElement.setAttribute('dir', 'ltr');
 *       document.documentElement.classList.add('dark');
 *       document.documentElement.classList.remove('light');
 *     }, []);
 *
 * None of them returned a cleanup function, and nothing anywhere removed the
 * class. So the first visit to Home, About, Contact, Personalised Learning or
 * Institution put the whole document in dark mode permanently, for every page
 * the reader opened afterwards in that session. Whether a page rendered light
 * or dark depended on which page they happened to land on first, which is why
 * the site rendered inconsistently across screens rather than wrongly on any
 * one of them.
 *
 * Restoring the previous state on unmount is the part that was missing. It is
 * also why this belongs in one place: five copies of a rule about global
 * document state is five chances to forget the half that puts it back.
 */
export default function useDarkPage() {
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    const hadLight = root.classList.contains('light');
    const previousDir = root.getAttribute('dir');

    root.setAttribute('dir', 'ltr');
    root.classList.add('dark');
    root.classList.remove('light');

    return () => {
      // Put back exactly what was there. Unconditionally removing `dark` would
      // be just as wrong in the other direction: it would strip the class off a
      // document that was already dark before this page mounted.
      if (!hadDark) root.classList.remove('dark');
      if (hadLight) root.classList.add('light');
      if (previousDir === null) root.removeAttribute('dir');
      else root.setAttribute('dir', previousDir);
    };
  }, []);
}
